import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Patient, Order } from './types';
import { useOrdersApi } from '../../hooks/useOrdersApi';
import { useSession } from "../../context/SessionContext";
import { useOrderDraft } from "../../context/OrderDraftContext";
import { Surgery } from '../../types/patient.types';
import { useDownloadGuiaInternacao, useDownloadOpme, useDownloadResumo, useDownloadRecurso } from '../../hooks/usePdfDownload';
import { opmeTemplateFor } from '../../lib/pdf';
import { INTERNACAO_PLANS, OPME_PLANS } from '../../constants/healthPlans';
import { HealthPlanPicker } from '../guides';
import { PatientDataDialog, PatientFormData } from './PatientDataDialog';
import { PatientListScreen } from './screens/PatientListScreen';
import { OrderListScreen } from './screens/OrderListScreen';
import { OrderDetailScreen, GuideKind } from './screens/OrderDetailScreen';
import { formatSurgeryRequest } from '../../lib/formatters';
import { surgeriesToOrders, surgeryRequestDataExtract } from './utils';
import { useCompanySelectionPrompt } from '../../hooks/useCompanySelectionPrompt';
import { CompanySelectDialog } from "../../components/CompanySelectDialog/CompanySelectDialog";
import { EmailComposer, WhatsAppComposer } from '../sharing';
import { Button, ConfirmDialog, Dialog } from '../../components/ui';
import { t } from '../../i18n';
import { PATHS } from "../../app/paths";

type Screen = 'patients' | 'orders' | 'detail';

export const SavedOrdersPage: React.FC = () => {
  const navigate = useNavigate();

  const { metadata } = useSession();
  const { setSurgerie, resetDraft } = useOrderDraft();

  const downloadInternacao = useDownloadGuiaInternacao();
  const downloadOpme = useDownloadOpme();
  const downloadResumo = useDownloadResumo();
  const downloadRecurso = useDownloadRecurso();
  const companySelection = useCompanySelectionPrompt();

  const {
    patients: patientNames,
    ordersCount,
    surgeries,
    loadingPatients,
    patientsFailed,
    loadingSurgeries,
    fetchPatientsByDoctor,
    fetchSurgeriesByPatient,
  } = useOrdersApi();

  const [screen, setScreen] = useState<Screen>('patients');
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [selectedSurgeryIndex, setSelectedSurgeryIndex] = useState<number>(0);
  const [guideChoice, setGuideChoice] = useState<GuideKind | null>(null);
  const [showPlanSelection, setShowPlanSelection] = useState(false);
  const [showOpmeSelection, setShowOpmeSelection] = useState(false);
  const [patientModalType, setPatientModalType] = useState<GuideKind | null>(null);
  const [pendingPatientData, setPendingPatientData] = useState<PatientFormData | null>(null);
  const [currentSurgery, setCurrentSurgery] = useState<Surgery | null>(null);
  const [showNoPedidoWarning, setShowNoPedidoWarning] = useState(false);
  const [showSendEmailModal, setShowSendEmailModal] = useState(false);
  const [showSendWhatsAppModal, setShowSendWhatsAppModal] = useState(false);

  useEffect(() => {
    fetchPatientsByDoctor();
  }, [fetchPatientsByDoctor]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [screen]);

  const patients: Patient[] = patientNames.map((name, i) => ({
    id: String(i),
    name,
    ordersCount: ordersCount?.[name] || 0,
  }));

  const orders: Order[] = surgeriesToOrders(surgeries);

  const handlePatientSelect = (patient: Patient) => {
    setSelectedPatient(patient);
    fetchSurgeriesByPatient(patient.name);
    setScreen('orders');
  };

  const handleOrderSelect = (order: Order) => {
    setSelectedOrder(order);
    const originalIndex = Number(order.id);
    setSelectedSurgeryIndex(originalIndex);
    // Preserve local edits when re-selecting the same order within the same session
    if (originalIndex !== selectedSurgeryIndex || !currentSurgery) {
      setCurrentSurgery(surgeries[originalIndex]);
    }
    setScreen('detail');
  };

  const handleEditOrder = () => {
    // Começa do zero: o pedido salvo não herda empresas, referências etc. do rascunho anterior.
    resetDraft();
    setSurgerie(surgeries[selectedSurgeryIndex]);
    navigate(PATHS.newOrder);
  };

  const patientSlug = currentSurgery
    ? (currentSurgery.pacient_name || '').split(' ').join('_').toLowerCase()
    : '';

  const handleDownloadGuia = async (plan: string, reportType: string) => {
    if (!currentSurgery) return;

    await downloadInternacao.download(
      {
        email: metadata.email,
        pedido_de_cirurgia: formatSurgeryRequest(surgeryRequestDataExtract(currentSurgery.pedido_cirurgia, currentSurgery.dataDesejada, currentSurgery.resumoOPME, currentSurgery.procedimentos_validados, currentSurgery.hospital)),
        template_type: plan,
        doctorName: metadata.nome,
        CRM: metadata.crm,
        desiredDate: currentSurgery.dataDesejada,
        documentNumber: pendingPatientData?.numCarteirinha ?? '',
        documentValidity: pendingPatientData?.valCarteirinha ?? '',
        // Resumida salva ao baixar a guia no fluxo principal; sem ela o backend
        // cai na descrição do próprio blob.
        descricao_resumida: currentSurgery.descricao_resumida ?? '',
      },
      `${patientSlug}${reportType}.pdf`
    );
    setPendingPatientData(null);
  };

  const handleDownloadOpme = async (plan: string, reportType: string) => {
    if (!currentSurgery) return;

    const companies = await companySelection.promptForCompanies();

    await downloadOpme.download(
      {
        email: metadata.email,
        template_type: opmeTemplateFor(plan),
        opme_data: currentSurgery.opme_list,
        pacientName: pendingPatientData?.nome ?? '',
        doctorName: metadata.nome,
        documentNumber: pendingPatientData?.numCarteirinha ?? '',
        justificativa_tecnica: currentSurgery.resumoOPME || '',
        observacoesOpme: currentSurgery.observacoesOpme || '',
        companies: companies,
      },
      `${patientSlug}${reportType}.pdf`
    );
    setPendingPatientData(null);
  };

  const checkHasPedidoCirurgia = (): boolean => {
    if (!currentSurgery?.pedido_cirurgia) {
      setShowNoPedidoWarning(true);
      return false;
    }
    return true;
  };

  const handleGuide = (kind: GuideKind) => {
    if (checkHasPedidoCirurgia()) setGuideChoice(kind);
  };

  // Só a guia: direto para o plano. Com dados do paciente: formulário antes.
  const handleGuideChoice = (withPatient: boolean) => {
    const kind = guideChoice;
    setGuideChoice(null);
    if (withPatient) setPatientModalType(kind);
    else if (kind === 'guia') setShowPlanSelection(true);
    else setShowOpmeSelection(true);
  };

  const handlePatientModalDownload = (formData: PatientFormData) => {
    setPendingPatientData(formData);
    setPatientModalType(null);
    if (patientModalType === 'guia') setShowPlanSelection(true);
    if (patientModalType === 'opme') setShowOpmeSelection(true);
  };

  const handlePatientFormChange = (formData: PatientFormData) => {
    setCurrentSurgery((prev) =>
      prev
        ? {
            ...prev,
            pacient_name: formData.nome,
            dataNasc: formData.dataNasc,
            sexo: formData.sexo,
            cpf: formData.cpf,
            convenio: formData.convenio,
            numCarteirinha: formData.numCarteirinha,
            valCarteirinha: formData.valCarteirinha,
          }
        : prev
    );
  };

  const handleDownloadResumo = async () => {
    if (!currentSurgery) return;
    if (!checkHasPedidoCirurgia()) return;

    const companies = await companySelection.promptForCompanies();
    const materiais_opme = (currentSurgery.opme_list || []).map(
      (item) => `${item[0]} x${item[1]}`
    );
    await downloadResumo.download(
      {
        email: metadata.email,
        pedido_de_cirurgia: currentSurgery.pedido_cirurgia,
        timestamp: currentSurgery.timestamp,
        materiais_opme,
        desiredDate: currentSurgery.dataDesejada,
        justificativa_tecnica: currentSurgery.resumoOPME || '',
        observacoesOpme: currentSurgery.observacoesOpme || '',
        companies: companies,
      },
      `${patientSlug}_resumo.pdf`
    );
  };

  const handleDownloadRecurso = async () => {
    if (!currentSurgery?.recurso) return;

    await downloadRecurso.download(
      currentSurgery.recurso,
      `${currentSurgery.recurso.payload.header.id}.pdf`
    );
  };

  const handleGoToRecursos = () => {
    navigate(PATHS.appeals, { state: { preselectedSurgery: currentSurgery } });
  };

  const patientData = currentSurgery
    ? {
        nome: currentSurgery.pacient_name,
        dataNasc: currentSurgery.dataNasc,
        sexo: currentSurgery.sexo,
        cpf: currentSurgery.cpf,
        convenio: currentSurgery.convenio,
        numCarteirinha: currentSurgery.numCarteirinha,
        valCarteirinha: currentSurgery.valCarteirinha,
      }
    : undefined;

  return (
    <>
      <div key={screen} className="animate-fade-in">
        {screen === 'patients' && (
          <PatientListScreen
            patients={patients}
            loading={loadingPatients}
            failed={patientsFailed}
            onRetry={fetchPatientsByDoctor}
            onPatientSelect={handlePatientSelect}
          />
        )}
        {screen === 'orders' && selectedPatient && (
          <OrderListScreen
            patient={selectedPatient}
            orders={orders}
            loading={loadingSurgeries}
            onOrderSelect={handleOrderSelect}
            onBack={() => setScreen('patients')}
          />
        )}
        {screen === 'detail' && selectedOrder && currentSurgery && (
          <OrderDetailScreen
            order={selectedOrder}
            surgery={currentSurgery}
            onBack={() => setScreen('orders')}
            onEditOrder={handleEditOrder}
            onGuide={handleGuide}
            onDownloadResumo={handleDownloadResumo}
            onDownloadRecurso={handleDownloadRecurso}
            onCreateAppeal={handleGoToRecursos}
            onOpenSendEmail={() => setShowSendEmailModal(true)}
            onOpenSendWhatsApp={() => setShowSendWhatsAppModal(true)}
            loadingGuia={downloadInternacao.loading}
            loadingOpme={downloadOpme.loading}
            loadingResumo={downloadResumo.loading}
            loadingRecurso={downloadRecurso.loading}
          />
        )}
      </div>

      <Dialog
        open={guideChoice !== null}
        onClose={() => setGuideChoice(null)}
        size="sm"
        title={t('savedOrders.guide.choiceTitle')}
        footer={
          <>
            <Button variant="secondary" onClick={() => handleGuideChoice(false)}>{t('savedOrders.guide.withoutPatient')}</Button>
            <Button variant="primary" onClick={() => handleGuideChoice(true)}>{t('savedOrders.guide.withPatient')}</Button>
          </>
        }
      >
        <p className="text-fg-muted">{t('savedOrders.guide.choiceHint')}</p>
      </Dialog>

      {patientModalType && (
        <PatientDataDialog
          onClose={() => setPatientModalType(null)}
          onDownload={handlePatientModalDownload}
          onFormChange={handlePatientFormChange}
          initialData={patientData}
          timestamp={currentSurgery?.timestamp}
        />
      )}
      {showPlanSelection && (
        <HealthPlanPicker
          title={t('guides.admissionTitle')}
          plans={INTERNACAO_PLANS}
          loading={downloadInternacao.loading}
          onClose={() => { setShowPlanSelection(false); setPendingPatientData(null); }}
          onDownload={handleDownloadGuia}
          reportType={pendingPatientData ? '_pedido_de_cirurgia_completo' : '_pedido_de_cirurgia_resumido'}
        />
      )}
      {showOpmeSelection && (
        <HealthPlanPicker
          title={t('guides.opmeTitle')}
          plans={OPME_PLANS}
          loading={downloadOpme.loading}
          onClose={() => { setShowOpmeSelection(false); setPendingPatientData(null); }}
          onDownload={handleDownloadOpme}
          reportType={pendingPatientData ? '_opme_completo' : '_opme_resumido'}
        />
      )}
      {companySelection.isPromptVisible && (
        <CompanySelectDialog
          companies={companySelection.companies}
          onConfirm={companySelection.handleConfirm}
          onClose={companySelection.handleClose}
        />
      )}
      {showSendEmailModal && (
        <EmailComposer
          onClose={() => setShowSendEmailModal(false)}
          patientName={currentSurgery?.pacient_name}
        />
      )}
      {showSendWhatsAppModal && (
        <WhatsAppComposer
          onClose={() => setShowSendWhatsAppModal(false)}
          patientName={currentSurgery?.pacient_name}
        />
      )}
      {showNoPedidoWarning && (
        <ConfirmDialog
          tone="default"
          title={t('savedOrders.noRequest.title')}
          message={t('savedOrders.noRequest.message')}
          confirmLabel={t('savedOrders.noRequest.action')}
          cancelLabel={t('savedOrders.noRequest.close')}
          onConfirm={() => { setShowNoPedidoWarning(false); handleEditOrder(); }}
          onCancel={() => setShowNoPedidoWarning(false)}
        />
      )}
    </>
  );
};
